/**
 * Chỉ mục người nhận của MỘT đợt thông báo phí: `FeeNotificationContract.Id` → danh sách địa chỉ nhận.
 *
 * <p><b>Vì sao đọc được TRƯỚC khi gửi.</b> Nguồn dữ liệu là bảng log `FeeNotificationContractLog`,
 * mà BE insert <b>ngay lúc tạo đợt</b> — `InsertDetailFeeNotificationJob.GenerationFeeNotificationLogAsync`
 * ghi sẵn `SentTo` với `Status = NEW` (địa chỉ lấy từ `ContactPersonEmail` trên hợp đồng + email trong
 * hồ sơ khách). Log KHÔNG phải sinh ra lúc gửi, nên chỉ mục này trả lời được câu hỏi "đợt này sẽ đi
 * tới những email nào" trước khi bấm Gửi.</p>
 *
 * <p><b>Vì sao gộp theo `feeNotificationContractId`.</b> Khoá này trùng đúng `id` của dòng trong bảng
 * "Danh sách HĐ cần thông báo" (cả hai đều là `FeeNotificationContract.Id`), nên không phải dò theo mã
 * hợp đồng — một hợp đồng có thể nằm ở nhiều đợt, mã HĐ không phải khoá duy nhất trong ngữ cảnh này.</p>
 *
 * <p><b>Vì sao phải gộp nhiều dòng.</b> `GetFeeNotificationHistory` trả <b>một dòng cho mỗi (hợp đồng,
 * khách hàng)</b>. Hợp đồng đồng sở hữu ⇒ nhiều dòng cùng `feeNotificationContractId`; mỗi dòng lại có
 * nhiều địa chỉ trong `lstEmailStatus`. Cột trên bảng cần MỘT danh sách phẳng đã khử trùng.</p>
 */

/** Một mục trong `lstEmailStatus` / `lstSMSStatus` của `FeeNotificationContractLogOutputDto`. */
export interface FeeNotificationLogStatus {
  sentTo?: string
  status?: string
  sendDate?: string
  /**
   * BE hiện KHÔNG set hai trường này ở nhánh `GetFeeNotificationHistory` (chỉ set ở
   * `GetFeeNotificationSendDetail`) — khai báo để nhận nếu BE bổ sung sau, và luôn có đường lùi về
   * thông tin khách ở cấp dòng.
   */
  customerId?: number
  customerName?: string
}

/** Một dòng của `GetFeeNotificationHistory` = một cặp (hợp đồng trong đợt, khách hàng). */
export interface FeeNotificationHistoryItem {
  feeNotificationContractId?: number
  contractId?: number
  customerId?: number
  customerFullName?: string
  /**
   * Lô thu được cảm ơn. `null` với dòng của đợt NHẮC NỢ (không gắn lô thu) hoặc phiếu tạo trước khi
   * BE sinh mã giao dịch — bốn trường dưới đây luôn null/không-null cùng nhau.
   */
  transactionCode?: string | null
  /** Mã các phiếu trong lô, nối bằng `", "`. */
  receiptCodes?: string | null
  /** TỔNG tiền của cả lô — KHÔNG phải tiền một phiếu. */
  receiptAmount?: number | null
  /** Ngày thu của lô (nhỏ nhất trong lô). */
  receiptActualDate?: string | null
  lstEmailStatus?: FeeNotificationLogStatus[]
  lstSMSStatus?: FeeNotificationLogStatus[]
}

/** Một người nhận đã khử trùng, đủ dữ liệu để render trên bảng và trong popover. */
export interface BatchRecipient {
  sentTo: string
  customerId?: number
  customerName?: string
  status?: string
  sendDate?: string
}

/** `feeNotificationContractId` → người nhận của dòng đó. */
export type BatchRecipientIndex = Record<number, BatchRecipient[]>

/** Khoá khử trùng địa chỉ: trim + hạ chữ thường — cùng quy ước với BE (Req 9.3). */
const dedupeKeyOf = (address: string): string => address.trim().toLowerCase()

/**
 * Gộp danh sách dòng log của cả đợt thành chỉ mục người nhận theo từng hợp đồng.
 *
 * Hàm THUẦN, không phụ thuộc MobX/React — mọi thứ khó của tính năng nằm ở đây để test được.
 *
 * @param items Toàn bộ `items` đã gom đủ trang từ `GetFeeNotificationHistory`.
 * @param channel Kênh cần lấy. `EMAIL` đọc `lstEmailStatus`, `SMS` đọc `lstSMSStatus`.
 * @returns Chỉ mục chỉ chứa hợp đồng THỰC SỰ có ít nhất một địa chỉ. Hợp đồng thiếu thông tin khách
 *   (BE đánh `GenerationStatus = FAIL`) sẽ vắng mặt — phía UI dựa vào đó để cảnh báo "chưa có email",
 *   nên KHÔNG tạo entry rỗng ở đây.
 */
export const buildRecipientIndex = (
  items?: FeeNotificationHistoryItem[],
  channel: "EMAIL" | "SMS" = "EMAIL"
): BatchRecipientIndex => {
  const index: BatchRecipientIndex = {}
  const seen = new Map<number, Set<string>>()

  for (const item of items ?? []) {
    const contractKey = item?.feeNotificationContractId
    if (!contractKey) continue

    const logs = channel === "SMS" ? item?.lstSMSStatus : item?.lstEmailStatus
    for (const log of logs ?? []) {
      const address = (log?.sentTo ?? "").trim()
      if (!address) continue

      let seenForContract = seen.get(contractKey)
      if (!seenForContract) {
        seenForContract = new Set<string>()
        seen.set(contractKey, seenForContract)
        index[contractKey] = []
      }

      const key = dedupeKeyOf(address)
      if (seenForContract.has(key)) continue
      seenForContract.add(key)

      index[contractKey].push({
        sentTo: address,
        customerId: log?.customerId ?? item?.customerId,
        customerName: log?.customerName ?? item?.customerFullName,
        status: log?.status,
        sendDate: log?.sendDate,
      })
    }
  }

  return index
}
